import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  TestTube2,
  Bot,
  Layout,
  Code2,
  CloudCog,
  LineChart,
  CheckCircle2,
  ChevronRight,
  Sparkles,
  ArrowRight,
} from "lucide-react";

import PageHero from "@/components/shared/PageHero";
import Section from "@/components/shared/Section";
import Reveal from "@/components/shared/Reveal";
import GlowCard from "@/components/shared/GlowCard";
import Eyebrow from "@/components/shared/Eyebrow";

const SOLUTIONS = [
  {
    icon: TestTube2,
    title: "Intelligent QA & Test Automation",
    description:
      "Deliver faster releases with confidence. Our automation-first QA approach ensures high-quality software with minimal risk.",
    deliverables: [
      "End-to-end test automation using Playwright, Selenium & Cypress",
      "CI/CD-integrated testing pipelines",
      "API & performance testing",
      "Cross-browser and mobile automation",
      "AI-assisted test case generation",
    ],
    impact: "Reduce regression time by up to 40% and catch defects early.",
    cta: "Optimize your testing",
  },
  {
    icon: Bot,
    title: "AI & Intelligent Automation",
    description:
      "Turn AI into a real business advantage. We build smart solutions that automate workflows and improve decision-making.",
    deliverables: [
      "Custom AI chatbots & virtual assistants",
      "Generative AI integrations",
      "AI-powered workflow automation",
      "NLP-based document processing",
      "AI-driven customer support tools",
    ],
    impact: "Cut operational workload by 30-50% with intelligent automation.",
    cta: "Explore AI solutions",
  },
  {
    icon: Layout,
    title: "UI/UX & Product Engineering",
    description:
      "Create intuitive, high-performing digital experiences that users love. We combine design thinking with engineering to build impactful products.",
    deliverables: [
      "User-centric UI/UX design",
      "Responsive web & mobile interfaces",
      "Design systems & component libraries",
      "Usability testing & optimization",
      "Frontend development with modern frameworks",
    ],
    impact: "Improve user engagement and conversions by up to 35%.",
    cta: "Enhance user experience",
  },
  {
    icon: Code2,
    title: "Custom Software Development",
    description:
      "We design and develop scalable, secure, and future-ready applications tailored to your business.",
    deliverables: [
      "Web & mobile application development",
      "SaaS product engineering",
      "API-first architecture",
      "Cloud-native development",
      "Secure and scalable solutions",
    ],
    impact: "Launch products faster with high performance and scalability.",
    cta: "Build your solution",
  },
  {
    icon: CloudCog,
    title: "Cloud & DevOps Solutions",
    description:
      "Accelerate deployment and improve reliability with modern DevOps practices.",
    deliverables: [
      "CI/CD pipeline setup",
      "Cloud deployment (AWS, Azure, GCP)",
      "Infrastructure as Code (IaC)",
      "Monitoring & performance optimization",
      "Cloud cost optimization",
    ],
    impact: "Deploy up to 5x faster with improved system stability.",
    cta: "Modernize your infrastructure",
  },
  {
    icon: LineChart,
    title: "Data & Analytics Solutions",
    description:
      "Transform raw data into actionable insights that drive growth.",
    deliverables: [
      "Business intelligence dashboards",
      "Real-time analytics solutions",
      "Data pipeline automation",
      "Predictive analytics",
      "Data warehousing",
    ],
    impact: "Enable smarter decisions with real-time data visibility.",
    cta: "Unlock your data",
  },
];

const ExploreSolutions = () => (
  <div className="min-h-screen text-[var(--text-primary)]">
    <PageHero
      eyebrow="Explore Solutions"
      eyebrowIcon={Sparkles}
      title={
        <>
          Outcome-focused <span className="gradient-text">solutions, end-to-end.</span>
        </>
      }
      description="Clear, measurable offerings built to modernize delivery, automate operations, and accelerate growth."
      actions={
        <>
          <Button asChild className="btn-primary rounded-full px-6 h-11">
            <Link to="/contact" className="inline-flex items-center gap-2">
              Talk to our team <ArrowRight className="w-4 h-4" />
            </Link>
          </Button>
          <Button asChild variant="outline" className="btn-outline rounded-full px-6 h-11">
            <Link to="/case-studies">See case studies</Link>
          </Button>
        </>
      }
    />

    <Section>
      <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
        {SOLUTIONS.map((s, i) => (
          <Reveal key={s.title} variant="up" delay={(i % 3) * 0.08}>
            <GlowCard className="p-7 flex flex-col h-full">
              <div className="flex items-start gap-4 mb-4">
                <div className="feature-icon feature-icon-lg flex-shrink-0">
                  <s.icon className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display font-semibold text-lg leading-tight">{s.title}</h3>
                </div>
              </div>

              <p className="text-[var(--text-secondary)] text-sm mb-5 leading-relaxed">
                {s.description}
              </p>

              <div className="flex-1">
                <p className="text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-3">
                  What we deliver
                </p>
                <ul className="space-y-2 mb-5">
                  {s.deliverables.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-[var(--text-secondary)]">
                      <CheckCircle2 className="w-4 h-4 text-[var(--brand-primary-light)] flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-xl bg-[color:var(--brand-primary)]/8 border border-[color:var(--border-brand)] px-4 py-3 mb-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-[var(--brand-primary-light)] mb-0.5">
                  Impact
                </p>
                <p className="text-sm text-[var(--text-primary)]">{s.impact}</p>
              </div>

              <Button asChild className="btn-primary rounded-full px-5 h-10 self-start">
                <Link to="/contact" className="inline-flex items-center gap-2 text-sm">
                  {s.cta} <ChevronRight className="w-4 h-4" />
                </Link>
              </Button>
            </GlowCard>
          </Reveal>
        ))}
      </div>
    </Section>

    {/* CTA */}
    <Section>
      <Reveal variant="up">
        <GlowCard variant="liquid" className="aurora p-10 text-center max-w-4xl mx-auto">
          <Eyebrow icon={Sparkles} className="mb-4">Get started</Eyebrow>
          <h2 className="font-display font-bold text-3xl sm:text-4xl mb-4 leading-tight">
            Not sure where to <span className="gradient-text">start?</span>
          </h2>
          <p className="text-[var(--text-secondary)] mb-7 max-w-xl mx-auto">
            Book a 30-min discovery call. We'll map your goals to a tailored, measurable AI roadmap.
          </p>
          <Button asChild className="btn-primary rounded-full px-8 h-12">
            <Link to="/contact" className="inline-flex items-center gap-2">
              Book a discovery call <ArrowRight className="w-4 h-4" />
            </Link>
          </Button>
        </GlowCard>
      </Reveal>
    </Section>
  </div>
);

export default ExploreSolutions;
